import { useState } from 'react';
import { Copy, Eye, EyeOff } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody, IconButton } from '@vnetwork/ui/primitives';
import { SplitView } from '@vnetwork/ui/layout';
import { DataTable, type ColumnDef } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import type { LiveStreamItem } from '@/api/media/live-live-streams-types';
import { DescList, DescItem, monoValue } from '../../components/DescList';
import { LMS_INGEST_HOST, LMS_DELIVERY_DOMAIN, withLmsIngestHost, splitPushUrl } from '../lms-endpoint-format';
import styles from './JobOverviewPanel.module.css';

const INPUT_PROTOCOL_LABEL: Record<string, string> = {
  rtmp: 'RTMP', srt: 'SRT', srt_unicast: 'SRT Unicast',
  mpegts: 'MPEG-TS', mpegts_udp_multicast: 'MPEG-TS UDP Multicast',
  mpegts_srt_unicast: 'MPEG-TS over SRT (Unicast)',
  rtp: 'MPEG-TS over RTP (Multicast)', mp3rtp: 'MP3 over RTP', mp3Rtmp: 'MP3 over RTMP',
};

const PID_TYPE_LABEL: Record<number, string> = { 1: 'Video', 2: 'Audio', 3: 'Subtitle' };

type PidItem = { id?: number; type: number; pid: string; language: string | null; is_default: boolean | null };
type UrlItem = { label: string; url: string };

function CopyBtn({ value, label }: { value: string; label: string }) {
  const toast = useToast();
  return (
    <IconButton
      variant="ghost" size="sm" aria-label={`Copy ${label}`}
      className={styles.copyBtn}
      onClick={() => { navigator.clipboard.writeText(value).catch(() => {}); toast.success(`${label} copied`); }}
    >
      <Copy size={13} />
    </IconButton>
  );
}

/** Mono value + copy affordance for identity fields and rail rows. */
function MonoCopy({ value, label }: { value: string; label: string }) {
  return (
    <span className={styles.monoCopy}>
      <code className={styles.monoCode} style={monoValue}>{value}</code>
      <CopyBtn value={value} label={label} />
    </span>
  );
}

/** Sensitive mono value with reveal toggle + copy. Copy always sends the real value. */
function SecretMonoCopy({
  value, label, revealed, onToggle,
}: { value: string; label: string; revealed: boolean; onToggle: () => void }) {
  const displayValue = revealed ? value : '•'.repeat(Math.min(value.length, 24));
  return (
    <span className={styles.secretRevealRow}>
      <code className={styles.monoCode} style={monoValue}>
        {displayValue}
      </code>
      <IconButton
        variant="ghost" size="sm"
        aria-label={revealed ? 'Hide stream key' : 'Reveal stream key'}
        className={styles.copyBtn}
        onClick={onToggle}
      >
        {revealed ? <EyeOff size={13} /> : <Eye size={13} />}
      </IconButton>
      <CopyBtn value={value} label={label} />
    </span>
  );
}

/** One output URL: preset label + truncated monospace URL (never overflows) + copy. */
function UrlRow({ label, url }: UrlItem) {
  return (
    <div className={styles.urlRow}>
      <span className={styles.urlLabel}>{label}</span>
      <code className={styles.urlCode}>{url}</code>
      <CopyBtn value={url} label={`${label} URL`} />
    </div>
  );
}

const PID_COLUMNS: ColumnDef<PidItem, unknown>[] = [
  { id: 'type', header: 'Type', cell: ({ row }) => PID_TYPE_LABEL[row.original.type] ?? row.original.type },
  { id: 'pid', header: 'PID', cell: ({ row }) => <code style={monoValue}>{row.original.pid || '—'}</code> },
  { id: 'language', header: 'Language', cell: ({ row }) => row.original.language || '—' },
  { id: 'default', header: 'Default', cell: ({ row }) => (row.original.is_default ? 'Yes' : '—') },
];

export function JobOverviewPanel({ stream }: { stream: LiveStreamItem }) {
  const [keyRevealed, setKeyRevealed] = useState(false);

  const meta = stream.provider_metadata ?? {};
  const manifestName = (meta.manifest_name as string) || '';
  type Outbound = { preset_name: string; type: string };
  const outbounds: Outbound[] = (meta.stream_rtmp_outbounds_not_deleted as Outbound[])
    ?? (meta.stream_rtmp_outbounds as Outbound[]) ?? [];

  const hlsOuts = outbounds.filter((o) => o.type === 'hls');
  const dashOuts = outbounds.filter((o) => o.type === 'dash');
  const rtmpOuts = outbounds.filter((o) => o.type === 'rtmp');
  const flvOuts = outbounds.filter((o) => o.type === 'http-flv');

  // Output URLs are shown as https://<DELIVERY-DOMAIN> + path for every output
  // type (provider host dropped; the customer fronts it with their own domain).
  const D = LMS_DELIVERY_DOMAIN;
  const hlsManifest = hlsOuts.length && manifestName
    ? `${D}/${stream.app_name}/${manifestName}.m3u8` : '';
  const dashManifest = dashOuts.length && manifestName
    ? `${D}/${stream.app_name}/${manifestName}.mpd` : '';

  const urlGroups: { name: string; items: UrlItem[] }[] = [
    { name: 'HLS', items: [
      ...(hlsManifest ? [{ label: 'Manifest', url: hlsManifest }] : []),
      ...hlsOuts.map((o) => ({ label: o.preset_name, url: `${D}/${stream.app_name}/${stream.stream_name}-${o.preset_name}.m3u8` })),
    ] },
    { name: 'DASH', items: [
      ...(dashManifest ? [{ label: 'Manifest', url: dashManifest }] : []),
      ...dashOuts.map((o) => ({ label: o.preset_name, url: `${D}/${stream.app_name}/${stream.stream_name}-${o.preset_name}.mpd` })),
    ] },
    { name: 'RTMP', items: rtmpOuts.map((o) => ({ label: o.preset_name, url: `${D}/${stream.app_name}/${stream.stream_name}-${o.preset_name}` })) },
    { name: 'HTTP-FLV', items: flvOuts.map((o) => ({ label: o.preset_name, url: `${D}/${stream.app_name}/${stream.stream_name}-${o.preset_name}.flv` })) },
  ].filter((g) => g.items.length > 0);

  const pids: PidItem[] = (meta.stream_pids as PidItem[]) ?? [];

  const fmtDate = (v?: string | number | null) =>
    v ? new Date(typeof v === 'number' ? v : v).toLocaleString('en-GB').replace(',', '') : undefined;

  // Advertise the push URL on the fixed LMS ingest host, then split it so the
  // base (rtmp://host/<app>) and the stream key (the tail after /<app>/) show in
  // their own fields. Fall back to the record's stream_key when there's no tail.
  const fullPushUrl = stream.push_url ? withLmsIngestHost(stream.push_url) : '';
  const { base: pushUrlBase, key: pushKey } = splitPushUrl(fullPushUrl, stream.app_name);
  const streamKeyValue = pushKey || stream.stream_key || '';

  // Rail: how producers ingest the stream — kept separate from identity so the
  // right column is filled, mirroring the load-balancer Connection rail.
  const rail = (
    <Card flat>
      <CardHeader><CardTitle>Ingest</CardTitle></CardHeader>
      <CardBody>
        <dl className={styles.railList}>
          {pushUrlBase && (
            <DescItem label="Push URL">
              <MonoCopy value={pushUrlBase} label="Push URL" />
            </DescItem>
          )}
          {streamKeyValue && (
            <DescItem label="Stream Key">
              <SecretMonoCopy
                value={streamKeyValue}
                label="Stream Key"
                revealed={keyRevealed}
                onToggle={() => setKeyRevealed((r) => !r)}
              />
            </DescItem>
          )}
          <DescItem label="Ingestion Domain">
            <MonoCopy value={LMS_INGEST_HOST} label="Ingestion Domain" />
          </DescItem>
        </dl>
      </CardBody>
    </Card>
  );

  return (
    <SplitView asideWidth="320px" aside={rail}>
      {/* Identity — status lives in the page-header pill, so it is not repeated. */}
      <Card flat className={styles.infoCard}>
        <CardHeader><CardTitle>Stream information</CardTitle></CardHeader>
        <CardBody>
          <DescList>
            <DescItem label="Name">{stream.name || '—'}</DescItem>
            <DescItem label="App Name">{stream.app_name || '—'}</DescItem>
            <DescItem label="Stream Name"><MonoCopy value={stream.stream_name} label="Stream Name" /></DescItem>
            <DescItem label="Stream Kind"><span style={{ textTransform: 'capitalize' }}>{stream.stream_kind || '—'}</span></DescItem>
            <DescItem label="Input Protocol">{INPUT_PROTOCOL_LABEL[stream.input_protocol] ?? stream.input_protocol ?? '—'}</DescItem>
            <DescItem label="Outputs"><span style={{ textTransform: 'uppercase' }}>{(stream.outputs ?? []).join(', ') || '—'}</span></DescItem>
            <DescItem label="Use GPU">{(stream.gpu_enabled || meta.gpu === 'gpu') ? 'Yes' : 'No'}</DescItem>
            <DescItem label="Created At">{fmtDate(stream.created_at) || '—'}</DescItem>
            {meta.started ? <DescItem label="Started At">{fmtDate(meta.started as number)}</DescItem> : null}
            {meta.ended ? <DescItem label="Ended At">{fmtDate(meta.ended as number)}</DescItem> : null}
            <DescItem label="ID"><MonoCopy value={stream.id} label="ID" /></DescItem>
          </DescList>
        </CardBody>
      </Card>

      <Card flat className={styles.infoCard}>
        <CardHeader><CardTitle>Output URLs</CardTitle></CardHeader>
        <CardBody>
          {urlGroups.length === 0 ? (
            <span className={styles.noUrls}>No output URLs configured.</span>
          ) : (
            <div className={styles.urlGroups}>
              {urlGroups.map((g) => (
                <div key={g.name}>
                  <div className={styles.urlGroupLabel}>{g.name}</div>
                  {g.items.map((it) => <UrlRow key={it.label} {...it} />)}
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {pids.length > 0 && (
        <Card flat className={styles.infoCard}>
          <CardHeader><CardTitle>Stream tracks</CardTitle></CardHeader>
          <CardBody>
            <DataTable columns={PID_COLUMNS} data={pids} getRowId={(p) => String(p.id ?? `${p.type}-${p.pid}`)} />
          </CardBody>
        </Card>
      )}
    </SplitView>
  );
}

export default JobOverviewPanel;
